import { Avatar, Badge, Card, Icon, Space, Typography } from '@sravni/react-design-system';
import { Bank } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import cn from 'classnames';
import React from 'react';

import type { Color } from 'commonTypes/api/scenarios/BaseTypes';

import styles from './OfferCardBase.module.scss';

interface IOfferCardProps {
  title: string;
  subtitle: string;
  logo: string;
  details: Array<{ label: string; value: string }>;
  button: React.ReactNode;
  info?: React.ReactNode;
  containerProps?: React.ComponentProps<typeof Space>;
  active?: boolean;
  activeTitle?: string;
  badges?: Array<{ title: string; variant: 'primary' | 'secondary'; color: Color }>;
  color?: 'green' | 'orange' | 'blue' | 'red' | 'dark' | 'light' | 'violet';
  className?: string;
}

export const OfferCardBase = ({
  title,
  subtitle,
  logo,
  details,
  button,
  info,
  containerProps = {},
  active,
  activeTitle,
  badges,
  color,
  className,
}: IOfferCardProps) => {
  const isMobile = useIsMobile();
  return (
    <Space direction="vertical">
      {active ? (
        <Card className={styles.active}>
          <Space
            size={4}
            className="h-color-L100"
          >
            <Typography.Text
              strong
              size={14}
            >
              {activeTitle}
            </Typography.Text>
          </Space>
        </Card>
      ) : null}

      <Card
        color={color}
        size={isMobile ? 16 : 24}
        className={cn(styles.card, { [styles.gradientBorder]: active }, className)}
      >
        <Space
          direction="vertical"
          size={16}
        >
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            size={isMobile ? 12 : 32}
            className={styles.containerCard}
            {...containerProps}
          >
            <Space
              size={isMobile ? 12 : 16}
              align={isMobile ? 'start' : 'center'}
            >
              {logo ? (
                <Avatar
                  size={36}
                  src={logo}
                />
              ) : (
                <Icon
                  size={28}
                  icon={<Bank />}
                />
              )}

              <Space
                size={isMobile ? 2 : 4}
                direction="vertical"
              >
                <Typography.Heading level={5}>{title}</Typography.Heading>

                <Typography.Text
                  className="h-color-D60"
                  size={12}
                >
                  {subtitle}
                </Typography.Text>
              </Space>
              {isMobile && button ? <Space className={styles.button}>{button}</Space> : null}
            </Space>

            <Space
              justify="end"
              className={styles.summaries}
              size={32}
            >
              {details.map((detail) => {
                return (
                  <Space
                    className={styles.detail}
                    size={4}
                    direction="vertical"
                    key={detail.label}
                  >
                    <Typography.Text
                      className={cn(styles.detailText, 'h-color-D60')}
                      size={12}
                    >
                      {detail.label}
                    </Typography.Text>

                    <Typography.Text
                      className={styles.detailText}
                      strong
                      size={16}
                    >
                      {detail.value}
                    </Typography.Text>
                  </Space>
                );
              })}
            </Space>

            {!isMobile && button ? <Space className={styles.button}>{button}</Space> : null}
          </Space>

          {badges?.length ? (
            <Space
              size={8}
              wrap
            >
              {badges?.map((badge) => (
                <Badge
                  key={badge.title}
                  {...badge}
                />
              ))}
            </Space>
          ) : null}

          {info}
        </Space>
      </Card>
    </Space>
  );
};
